import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";
import ImageUploadField from "./ImageUploadField";

function createSlug(value) {
    return value
        .toLowerCase()
        .trim()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function AdminLitterFormPage({
                                                primaryColor = "#8D6E63",
                                                secondaryColor = "#A1887F",
                                            }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditing = Boolean(id);

    const [dogs, setDogs] = useState([]);

    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [birthDate, setBirthDate] = useState("");
    const [countLabel, setCountLabel] = useState("");
    const [description, setDescription] = useState("");
    const [groupImage, setGroupImage] = useState("");
    const [motherId, setMotherId] = useState("");
    const [fatherId, setFatherId] = useState("");
    const [isVisible, setIsVisible] = useState(true);
    const [sortOrder, setSortOrder] = useState(0);

    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const mothers = useMemo(
        () => dogs.filter((dog) => dog.sex === "Hündin"),
        [dogs]
    );

    const fathers = useMemo(
        () => dogs.filter((dog) => dog.sex === "Rüde"),
        [dogs]
    );

    const loadDogs = useCallback(async () => {
        const { data, error } = await supabase
            .from("dogs")
            .select("id, name, sex")
            .order("sort_order", { ascending: true })
            .order("name", { ascending: true });

        if (error) {
            console.error("Fehler beim Laden der Hunde:", error);
            return;
        }

        setDogs(data || []);
    }, []);

    const loadLitter = useCallback(async () => {
        if (!id) return;

        setIsLoading(true);
        setErrorMessage("");

        const { data, error } = await supabase
            .from("litters")
            .select("*")
            .eq("id", id)
            .single();

        if (error) {
            console.error("Fehler beim Laden des Wurfs:", error);
            setErrorMessage("Der Wurf konnte nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        setName(data.name || "");
        setSlug(data.slug || "");
        setBirthDate(data.birth_date || "");
        setCountLabel(data.count_label || "");
        setDescription(data.description || "");
        setGroupImage(data.group_image || "");
        setMotherId(data.mother_id || "");
        setFatherId(data.father_id || "");
        setIsVisible(Boolean(data.is_visible));
        setSortOrder(data.sort_order ?? 0);

        setIsLoading(false);
    }, [id]);

    useEffect(() => {
        loadDogs();

        if (isEditing) {
            loadLitter();
        } else {
            setIsLoading(false);
        }
    }, [isEditing, loadDogs, loadLitter]);

    function handleNameChange(value) {
        setName(value);

        if (!isEditing || !slug) {
            setSlug(createSlug(value));
        }
    }

    async function handleSubmit(event) {
        event.preventDefault();

        if (!name.trim()) {
            setErrorMessage("Bitte gib einen Namen für den Wurf ein.");
            return;
        }

        if (!slug.trim()) {
            setErrorMessage("Bitte gib einen Slug ein.");
            return;
        }

        setIsSaving(true);
        setErrorMessage("");

        const payload = {
            name: name.trim(),
            slug: slug.trim(),
            birth_date: birthDate || null,
            count_label: countLabel.trim() || null,
            description: description.trim() || null,
            group_image: groupImage.trim() || null,
            mother_id: motherId || null,
            father_id: fatherId || null,
            is_visible: isVisible,
            sort_order: Number(sortOrder) || 0,
            updated_at: new Date().toISOString(),
        };

        if (isEditing) {
            const { error } = await supabase
                .from("litters")
                .update(payload)
                .eq("id", id);

            setIsSaving(false);

            if (error) {
                console.error("Fehler beim Speichern:", error);
                setErrorMessage(
                    "Der Wurf konnte nicht gespeichert werden. Prüfe, ob der Slug bereits vergeben ist."
                );
                return;
            }

            navigate("/admin/litters");
            return;
        }

        const { error } = await supabase.from("litters").insert([
            {
                ...payload,
                created_at: new Date().toISOString(),
            },
        ]);

        setIsSaving(false);

        if (error) {
            console.error("Fehler beim Erstellen:", error);
            setErrorMessage(
                "Der Wurf konnte nicht erstellt werden. Prüfe, ob der Slug bereits vergeben ist."
            );
            return;
        }

        navigate("/admin/litters");
    }

    if (isLoading) {
        return (
            <main style={adminStyles.dashboardPage}>
                <div style={adminStyles.dashboardContainer}>
                    <p style={{ color: "#666" }}>Wurf wird geladen...</p>
                </div>
            </main>
        );
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            {isEditing ? "Wurf bearbeiten" : "Neuer Wurf"}
                        </h1>
                        <p style={adminStyles.subtitle}>
                            Lege die Grunddaten des Wurfs an. Die einzelnen Welpen werden danach separat verwaltet.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/admin/litters")}
                        style={{
                            ...adminStyles.secondaryButton,
                            backgroundColor: secondaryColor,
                        }}
                    >
                        Zurück
                    </button>
                </header>

                <motion.form
                    onSubmit={handleSubmit}
                    style={{
                        ...adminStyles.dashboardCard,
                        maxWidth: "900px",
                    }}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                            gap: "1rem",
                        }}
                    >
                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="name">
                                Name des Wurfs
                            </label>
                            <input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(event) => handleNameChange(event.target.value)}
                                style={adminStyles.input}
                                placeholder="z. B. A-Wurf"
                            />
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="slug">
                                Slug
                            </label>
                            <input
                                id="slug"
                                type="text"
                                value={slug}
                                onChange={(event) =>
                                    setSlug(createSlug(event.target.value))
                                }
                                style={adminStyles.input}
                                placeholder="z. B. a-wurf"
                            />
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="birthDate">
                                Geburtsdatum
                            </label>
                            <input
                                id="birthDate"
                                type="date"
                                value={birthDate}
                                onChange={(event) => setBirthDate(event.target.value)}
                                style={adminStyles.input}
                            />
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="sortOrder">
                                Sortierung
                            </label>
                            <input
                                id="sortOrder"
                                type="number"
                                value={sortOrder}
                                onChange={(event) => setSortOrder(event.target.value)}
                                style={adminStyles.input}
                            />
                        </div>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                            gap: "1rem",
                        }}
                    >
                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="motherId">
                                Mutter
                            </label>
                            <select
                                id="motherId"
                                value={motherId}
                                onChange={(event) => setMotherId(event.target.value)}
                                style={adminStyles.input}
                            >
                                <option value="">Keine Mutter auswählen</option>
                                {mothers.map((dog) => (
                                    <option key={dog.id} value={dog.id}>
                                        {dog.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="fatherId">
                                Vater
                            </label>
                            <select
                                id="fatherId"
                                value={fatherId}
                                onChange={(event) => setFatherId(event.target.value)}
                                style={adminStyles.input}
                            >
                                <option value="">Keinen Vater auswählen</option>
                                {fathers.map((dog) => (
                                    <option key={dog.id} value={dog.id}>
                                        {dog.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="countLabel">
                            Anzahl / Beschreibung der Welpen
                        </label>
                        <input
                            id="countLabel"
                            type="text"
                            value={countLabel}
                            onChange={(event) => setCountLabel(event.target.value)}
                            style={adminStyles.input}
                            placeholder="z. B. 6 Welpen (2 Rüden, 4 Hündinnen)"
                        />
                    </div>

                    <ImageUploadField
                        label="Gruppenbild optional"
                        value={groupImage}
                        onChange={setGroupImage}
                        folder="litters"
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        previewAlt={name || "Gruppenbild"}
                    />

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="description">
                            Beschreibung
                        </label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "170px",
                                resize: "vertical",
                                lineHeight: 1.7,
                                fontFamily: "inherit",
                            }}
                            placeholder="Beschreibung des Wurfs."
                        />
                    </div>

                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.7rem",
                            marginBottom: "1.5rem",
                            cursor: "pointer",
                            color: "#444",
                            lineHeight: 1.5,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={isVisible}
                            onChange={(event) => setIsVisible(event.target.checked)}
                            style={{
                                width: "18px",
                                height: "18px",
                                accentColor: primaryColor,
                            }}
                        />
                        Wurf auf der Webseite anzeigen
                    </label>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <motion.button
                            type="submit"
                            disabled={isSaving}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                opacity: isSaving ? 0.7 : 1,
                            }}
                            whileHover={{
                                scale: isSaving ? 1 : 1.03,
                                backgroundColor: secondaryColor,
                            }}
                            whileTap={{ scale: isSaving ? 1 : 0.97 }}
                        >
                            {isSaving
                                ? "Wird gespeichert..."
                                : isEditing
                                    ? "Änderungen speichern"
                                    : "Wurf erstellen"}
                        </motion.button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/litters")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: "#777",
                            }}
                        >
                            Abbrechen
                        </button>
                    </div>
                </motion.form>
            </div>
        </main>
    );
}